<template>
  <div class="virtual-tree-root">
    <div v-if="selectionMode" class="selection-panel">
      <div class="selection-panel__left">
        <span ref="deleteBtnRef" class="selection-panel__btn" @click="deleteSelected">Удалить</span>
        <span class="selection-panel__btn" @click="resetSelection">Сбросить</span>
      </div>
      <span class="selection-panel__btn" @click="exitSelectionMode">Отмена</span>
    </div>
    <!-- Пустой результат поиска: «Нет совпадений» по центру по горизонтали, 12px от верха
         (т.е. от внешней шапки .list-toolbar в Analytics.vue). Показываем только когда
         поиск активен и список после фильтрации пуст; «пустое дерево без поиска» — другой
         кейс, его поведение оставляем как было. -->
    <div
      v-if="searchActive && flatItems.length === 0"
      class="virtual-tree__no-results"
    >
      {{ $t('no_matches') }}
    </div>
    <div v-else class="virtual-tree-body">
      <!-- Полоса «в корень» слева: паддинг 16px, при наведении — пунктир. -->
      <div
        v-if="!betweenRootDropViaIndicator"
        :ref="rootDropLeftRef"
        class="root-drop-strip root-drop-strip--left"
        :class="{ 'root-drop-strip--over': rootDropLeftOver }"
      />
      <!-- Скролл: контент + справа полоса «в корень» (между элементами и скроллбаром). -->
      <div ref="scrollEl" class="virtual-tree-scroll" :class="{ 'virtual-tree-scroll--compact': selectionMode }">
        <div class="virtual-tree-scroll-inner" :style="{ minHeight: totalSize + 'px' }">
          <div class="virtual-tree-scroll-content" :style="{ height: totalSize + 'px' }">
          <div :style="{ height: totalSize + 'px', width: '100%', position: 'relative' }">
            <div
              v-for="row in safeVirtualRows"
              :key="row.flat.key"
              :class="{ 'vrow-animate': insertAnimActive }"
              :data-index="row.vRow.index"
              :ref="measureElement"
              :style="{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${row.vRow.start}px)`,
              }"
            >
              <base-list-element-lite
                :element="row.flat.item"
                :depth="row.flat.depth"
                :is-root="row.flat.isRoot"
                :rename-siblings="row.flat.levelSiblings"
                :has-children="row.flat.hasChildren"
                :effective-expanded="row.flat.effectiveExpanded"
                :input-index="row.flat.siblingIndex"
                :parent-id="row.flat.parentId"
                :parent-type="row.flat.parentType"
                :in-report-context="row.flat.inReportContext"
                :into-forbidden="intoForbiddenFn"
                :scroll-viewport="scrollEl"
                :allow-highlight-on-hover="allowHighlightOnHover"
                :allow-hover-action="allowRootToRootDropAction"
                :allow-child-to-child-drop-action="allowChildToChildDropAction"
                :allow-child-to-root-drop-action="allowChildToRootDropAction"
                :allow-root-to-child-drop-action="allowRootToChildDropAction"
                :selection-mode="selectionMode"
                :is-checked="isEffectivelySelected(row.flat.item)"
                :is-inherited="isInheritedSelected(row.flat.item)"
                :is-create-folder-pending-for-this-parent="!!pendingCreateFolderByParent?.[row.flat.item.FolderId]"
                :is-move-pending-for-this-item="!!pendingMoveByKey?.[makeSelectionKey(row.flat.item)]"
                :between-root-drop-via-indicator="betweenRootDropViaIndicator"
                :can-drop-to-root-for-drag="canDropToRootForDrag"
                @toggle-expand="onToggleExpand"
                @change-parent="onChangeParent"
                @card-selected="(arg: any) => emit('cardSelected', arg)"
                @card-deleted="(el: any, pos: any) => emit('cardDeleted', el, pos)"
                @card-favorite="(arg: any) => emit('cardFavorite', arg)"
                @card-cloned="(arg: any) => emit('cardCloned', arg)"
                @card-renamed="(arg: any) => emit('cardRenamed', arg)"
                @add-child-selection="(arg: any) => emit('addChildSelection', arg)"
                @add-child-projection="(arg: any) => emit('addChildProjection', arg)"
                @add-child-list="(arg: any) => emit('addChildList', arg)"
                @add-child-chart="(arg: any) => emit('addChildChart', arg)"
                @add-child-folder="(arg: any) => emit('addChildFolder', arg)"
                @add-to-report="(arg: any) => emit('addToReport', arg)"
                @enter-selection-mode="enterSelectionMode"
                @toggle-item-selection="onToggleItemSelection"
                @switch-projection="(arg: any) => emit('cardSelected', { element: arg.element, switch: true, segment: arg.isRoot ? arg.element.SrcType : arg.element.DestType, scrollto: true })"
                @break-after="(arg: any) => emit('breakAfter', arg)"
                @change-position="(arg: any) => emit('changePosition', arg)"
                @card-moved="(arg: any) => emit('cardMoved', arg)"
              />
            </div>
          </div>
          </div>
          <!-- Полоса «в корень» справа: между списком и скроллбаром. -->
          <div
            v-if="!betweenRootDropViaIndicator"
            :ref="rootDropRightRef"
            class="root-drop-strip root-drop-strip--right"
            :class="{ 'root-drop-strip--over': rootDropRightOver }"
          />
        </div>
      </div>
    </div>
    <ConfirmActionPopup
      v-if="confirmPopup.show"
      :title="confirmPopup.title"
      :question="confirmPopup.question"
      :confirm-label="confirmPopup.confirmLabel"
      :cancel-label="confirmPopup.cancelLabel"
      :position="confirmPopup.position"
      @confirm="confirmPopup.onConfirm"
      @cancel="confirmPopup.onCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, toRaw, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useDrop } from 'vue3-dnd';
import { useVirtualizer } from '@tanstack/vue-virtual';
import BaseListElementLite from './BaseListElementLite.vue';
import ConfirmActionPopup from '@/components/common/basic/confirm/ConfirmActionPopup.vue';
import { BaseListItem } from './interfaces/ListTypes';
import { BaseItemTypes, DragItemTypes } from '@/components/dnd/ItemTypes';
import { i18n } from '@/i18n';
import { generalStore } from '@/store/general';
import { searchVersionRef } from './searchState';
import { insertAnimActive } from './insertAnimState';
import {
  CHILD_ARRAYS,
  canDropToRoot,
  isFolderDropForbidden,
  isImmediateParentByIdentity,
  allowChildToChildDropAction as _allowChildToChildDropAction,
  allowChildToRootDropAction as _allowChildToRootDropAction,
  allowRootToRootDropAction as _allowRootToRootDropAction,
  allowRootToChildDropAction as _allowRootToChildDropAction,
  allowHighlightOnHover as _allowHighlightOnHover,
} from './dndRules';

const props = defineProps<{
  inputItems: BaseListItem[];
  searchString?: string;
  favoriteMode?: boolean;
  pendingCreateFolderByParent?: Record<number, boolean>;
  pendingMoveByKey?: Record<string, boolean>;
  /** Опция: полоска между элементами = drop в корень; боковые полосы скрываются. */
  betweenRootDropViaIndicator?: boolean;
}>();

const betweenRootDropViaIndicator = computed(() => !!props.betweenRootDropViaIndicator);

const emit = defineEmits<{
  (e: 'cardSelected', arg: any): void;
  (e: 'cardDeleted', arg: any, anchorPos?: { x: number; y: number }): void;
  (e: 'cardFavorite', arg: any): void;
  (e: 'cardRenamed', arg: any): void;
  (e: 'cardCloned', arg: any): void;
  (e: 'changeParent', arg: any): void;
  (e: 'addChildSelection', arg: any): void;
  (e: 'addChildProjection', arg: any): void;
  (e: 'addChildList', arg: any): void;
  (e: 'addChildChart', arg: any): void;
  (e: 'addChildFolder', arg: any): void;
  (e: 'addToReport', arg: any): void;
  (e: 'deleteSelected', identities: any[], anchorPos: { x: number; y: number }): void;
  (e: 'breakAfter', arg: any): void;
  (e: 'changePosition', arg: any): void;
  (e: 'cardMoved', arg: any): void;
}>();

const scrollEl = ref<HTMLElement | null>(null);
const deleteBtnRef = ref<HTMLElement | null>(null);
const genstore = generalStore();

// --- Мультивыбор элементов ---

const selectionMode = ref(false);

/**
 * Карта выбранных элементов. Ключ — составная строка Identity (elType:Id:FolderId:SrcType:DestType),
 * значение — полный объект callbackParams-формата, совместимый с canDropElement/canDropToRoot из dndRules.ts.
 * При будущем мультидраге: for (item of selectedItems.values()) canDropElement(item, hoverEl).
 */
const selectedItems: Map<string, any> = reactive(new Map());

const lastClickPos = ref({ x: 0, y: 0 });
function trackClickPos(e: MouseEvent) { lastClickPos.value = { x: e.clientX, y: e.clientY }; }
onMounted(() => document.addEventListener('mousedown', trackClickPos, true));
onBeforeUnmount(() => document.removeEventListener('mousedown', trackClickPos, true));

// Одноразовая подсветка элемента при переходе по ссылке гасится первым кликом мыши где угодно.
// Слушатель навешиваем ТОЛЬКО когда подсветка реально активна и с отложкой (setTimeout 0),
// чтобы не поймать тот же mousedown-жест, которым открыли элемент (иначе подсветка гаснет мгновенно).
let clearHighlightArmed = false;
function onClearHighlightMousedown() {
  genstore.clearLinkHighlight();
  disarmClearHighlight();
}
function armClearHighlight() {
  if (clearHighlightArmed) return;
  clearHighlightArmed = true;
  setTimeout(() => {
    if (!genstore.linkHighlight) { clearHighlightArmed = false; return; }
    document.addEventListener('mousedown', onClearHighlightMousedown, true);
  }, 0);
}
function disarmClearHighlight() {
  document.removeEventListener('mousedown', onClearHighlightMousedown, true);
  clearHighlightArmed = false;
}
watch(
  () => genstore.linkHighlight,
  (hl) => { if (hl) armClearHighlight(); else disarmClearHighlight(); },
  { immediate: true },
);
onBeforeUnmount(() => disarmClearHighlight());

const confirmPopup = reactive({
  show: false,
  position: { x: 0, y: 0 },
  title: '',
  question: '',
  confirmLabel: '',
  cancelLabel: '',
  onConfirm: () => {},
  onCancel: () => {},
});

/** Универсальный ключ полной Identity: для папок учитывается FolderIdentity, для остальных — полный AnalyticsElementIdentity. */
function makeSelectionKey(p: any): string {
  const elType = p.elType ?? '';
  const id = p.id ?? p.Id ?? '';
  const folderId = p.folderId ?? p.FolderId ?? '';
  const src = p.sourceDataType ?? p.SrcType ?? '';
  const dest = p.destinationDataType ?? p.DestType ?? '';
  // Для дочерних элементов в отчётах одна и та же сущность (Id+elType+SrcType+DestType)
  // может встречаться в разных отчётах; чтобы различать конкретные ссылки, добавляем ElementId.
  const innerElementId = p.innerElementId ?? p.ElementId ?? '';
  return `${elType}:${id}:${folderId}:${src}:${dest}:${innerElementId}`;
}

/** Конвертирует BaseListItem (PascalCase) в camelCase-формат callbackParams для единообразного хранения в selectedItems. */
function toCallbackParams(item: BaseListItem): any {
  const fi = flatItems.value.find(f => makeSelectionKey(f.item) === makeSelectionKey(item));
  return {
    id: item.Id,
    folderId: item.FolderId,
    parent_id: fi?.parentId,
    parent_type: fi?.parentType,
    isRoot: fi?.isRoot ?? false,
    elType: item.elType,
    sourceDataType: item.SrcType,
    destinationDataType: item.DestType,
    innerElementId: (item as any).ElementId,
  };
}

/**
 * Собирает ВСЕ дочерние элементы (рекурсивно по CHILD_ARRAYS) из узла дерева.
 * Возвращает массив самих BaseListItem потомков (любой глубины).
 */
function getAllDescendants(item: BaseListItem): BaseListItem[] {
  const result: BaseListItem[] = [];
  for (const key of CHILD_ARRAYS) {
    const arr = (item as any)[key] as BaseListItem[] | undefined;
    if (!arr) continue;
    for (const child of arr) {
      result.push(child);
      result.push(...getAllDescendants(child));
    }
  }
  return result;
}

/**
 * Возвращает ключи всех потомков элемента (для быстрого удаления из selectedItems).
 */
function getAllDescendantKeys(item: BaseListItem): string[] {
  return getAllDescendants(item).map(d => makeSelectionKey(d));
}

/**
 * Собирает прямых детей элемента (один уровень, все CHILD_ARRAYS).
 */
function getDirectChildren(item: BaseListItem): BaseListItem[] {
  const result: BaseListItem[] = [];
  for (const key of CHILD_ARRAYS) {
    const arr = (item as any)[key] as BaseListItem[] | undefined;
    if (arr) result.push(...arr);
  }
  return result;
}

/**
 * Ищет BaseListItem в flatItems по ключу (callbackParams или BaseListItem).
 * Нужно, чтобы из callbackParams (camelCase) получить узел дерева с дочерними массивами.
 */
function findTreeItemByKey(key: string): BaseListItem | null {
  const fi = flatItems.value.find(f => makeSelectionKey(f.item) === key);
  return fi?.item ?? null;
}

/**
 * Ищет FlatItem в flatItems по ключу.
 */
function findFlatItemByKey(key: string): FlatItem | null {
  return flatItems.value.find(f => makeSelectionKey(f.item) === key) ?? null;
}

/**
 * Проверяет, является ли элемент потомком (на любом уровне) другого элемента.
 * Работает по дереву (CHILD_ARRAYS), а не по flatItems, чтобы покрыть свёрнутые ветки.
 */
function isDescendantOf(descendant: BaseListItem, ancestor: BaseListItem): boolean {
  const descKey = makeSelectionKey(descendant);
  return getAllDescendantKeys(ancestor).includes(descKey);
}

/**
 * Находит реально выбранного предка (из selectedItems) для данного элемента.
 * Идёт вверх по дереву через flatItems.parentId/parentType.
 * Возвращает FlatItem предка или null, если предок не выбран.
 */
function findSelectedAncestor(itemKey: string): FlatItem | null {
  const fi = findFlatItemByKey(itemKey);
  if (!fi || fi.parentId == null) return null;

  // Поднимаемся по дереву вверх через parentId/parentType.
  // Для папок сравниваем по FolderId, для остальных — по Id (без упрощений).
  let current = fi;
  while (current.parentId != null) {
    const parentFi = flatItems.value.find(f => {
      if (f.item.elType !== current.parentType) return false;
      if (current.parentType === BaseItemTypes.FOLDER) {
        return (f.item as any).FolderId === current.parentId;
      }
      return (f.item as any).Id === current.parentId;
    });
    if (!parentFi) break;
    const parentKey = makeSelectionKey(parentFi.item);
    if (selectedItems.has(parentKey)) return parentFi;
    current = parentFi;
  }
  return null;
}

/**
 * «Взрыв» выделения предка: когда пользователь снимает галку с унаследованного элемента,
 * предок убирается из selectedItems, а все ветки дерева между предком и снятым элементом
 * «расщепляются» — каждый сиблинг на пути становится реальным выделением.
 *
 * Пример: A (real) -> B1, B2, B3; B2 -> C1, C2, C3. Пользователь снимает C2.
 * Результат: A убирается. B1, B3 добавляются. C1, C3 добавляются. C2 — нет.
 */
function explodeSelection(ancestorItem: BaseListItem, uncheckedItem: BaseListItem) {
  // Строим путь от предка к снятому элементу через дерево
  const path: BaseListItem[] = [];

  function buildPath(current: BaseListItem, target: BaseListItem): boolean {
    path.push(current);
    if (makeSelectionKey(current) === makeSelectionKey(target)) return true;
    for (const key of CHILD_ARRAYS) {
      const arr = (current as any)[key] as BaseListItem[] | undefined;
      if (!arr) continue;
      for (const child of arr) {
        if (buildPath(child, target)) return true;
      }
    }
    path.pop();
    return false;
  }

  buildPath(ancestorItem, uncheckedItem);

  // На каждом уровне пути добавляем сиблингов (не входящих в путь) как реальные выделения
  for (let i = 0; i < path.length - 1; i++) {
    const parent = path[i];
    const nextOnPath = path[i + 1];
    const nextOnPathKey = makeSelectionKey(nextOnPath);
    const children = getDirectChildren(parent);
    for (const child of children) {
      if (makeSelectionKey(child) !== nextOnPathKey) {
        selectedItems.set(makeSelectionKey(child), toCallbackParams(child));
      }
    }
  }
}

/**
 * Проверяет, выбран ли элемент унаследованно (не сам, а через предка).
 */
function isInheritedSelected(item: BaseListItem): boolean {
  const key = makeSelectionKey(item);
  if (selectedItems.has(key)) return false; // реально выбран, не унаследован
  return findSelectedAncestor(key) !== null;
}

/**
 * Проверяет, выбран ли элемент эффективно (реально ИЛИ унаследованно).
 */
function isEffectivelySelected(item: BaseListItem): boolean {
  const key = makeSelectionKey(item);
  return selectedItems.has(key) || findSelectedAncestor(key) !== null;
}

function enterSelectionMode(initialParams?: any) {
  // Массовое выделение недоступно в дереве отчётов: ни сам REPORT, ни его потомки
  // не должны участвовать в multi-select.
  if (initialParams?.elType === BaseItemTypes.REPORT) return;
  selectionMode.value = true;
  if (initialParams) {
    handleCheck(initialParams);
  }
}

function exitSelectionMode() {
  selectionMode.value = false;
  selectedItems.clear();
}

function resetSelection() {
  selectedItems.clear();
}

/**
 * Обработка включения галки: добавляем элемент, убираем всех потомков
 * (родитель покрывает их).
 */
function handleCheck(params: any) {
  const key = makeSelectionKey(params);
  const treeItem = findTreeItemByKey(key);

  selectedItems.set(key, params);

  if (treeItem) {
    for (const dk of getAllDescendantKeys(treeItem)) {
      selectedItems.delete(dk);
    }
  }
}

/**
 * Обработка снятия галки: если элемент реально выбран — просто убираем.
 * Если унаследован — «взрываем» выделение предка, расщепляя на сиблингов.
 */
function handleUncheck(params: any) {
  const key = makeSelectionKey(params);

  if (selectedItems.has(key)) {
    const treeItem = findTreeItemByKey(key);
    const children = treeItem ? getDirectChildren(treeItem) : [];

    // Упрощённый сценарий по требованию: при снятии родителя ведём себя как "Да" в старом подтверждении.
    selectedItems.delete(key);
    if (children.length === 0) {
      return;
    }
  } else {
    const ancestorFi = findSelectedAncestor(key);
    if (!ancestorFi) return;
    const ancestorKey = makeSelectionKey(ancestorFi.item);
    selectedItems.delete(ancestorKey);

    const treeItem = findTreeItemByKey(key);
    if (treeItem) {
      explodeSelection(ancestorFi.item, treeItem);
    }
  }
}

function onToggleItemSelection(params: any) {
  const key = makeSelectionKey(params);
  const effectivelySelected = selectedItems.has(key) || findSelectedAncestor(key) !== null;

  if (effectivelySelected) {
    handleUncheck(params);
  } else {
    handleCheck(params);
  }
}

function deleteSelected() {
  const rect = deleteBtnRef.value?.getBoundingClientRect();
  const pos = rect ? { x: rect.left, y: rect.bottom + 4 } : { x: 0, y: 0 };
  emit('deleteSelected', Array.from(selectedItems.values()), pos);
}

function getSelectedIdentities(): any[] {
  return Array.from(selectedItems.values());
}

/**
 * Возвращает эффективный набор переносимых элементов:
 * - реально выбранные (зелёные),
 * - их дочерние (синие информационные),
 * без дублей по полной Identity.
 */
function getEffectiveSelectedIdentities(): any[] {
  const result: any[] = [];
  const used = new Set<string>();

  for (const [, val] of selectedItems) {
    const key = makeSelectionKey(val);
    if (!used.has(key)) {
      used.add(key);
      result.push(val);
    }

    const treeItem = findTreeItemByKey(key);
    if (!treeItem) continue;
    const descendants = getAllDescendants(treeItem);
    for (const child of descendants) {
      const childParams = toCallbackParams(child);
      const childKey = makeSelectionKey(childParams);
      if (used.has(childKey)) continue;
      used.add(childKey);
      result.push(childParams);
    }
  }

  return result;
}

/**
 * Общее количество эффективно выбранных элементов (реальные + все их потомки).
 * Используется в счётчике drag-превью: показываем полное число затрагиваемых элементов.
 */
const effectiveSelectedCount = computed(() => {
  let count = 0;
  for (const [, val] of selectedItems) {
    count += 1; // сам элемент
    const treeItem = findTreeItemByKey(makeSelectionKey(val));
    if (treeItem) count += getAllDescendants(treeItem).length;
  }
  return count;
});

/**
 * Определяет DnD-тип элемента (CARD_ROOT / CARD_CHILD) по его callbackParams.
 * Логика аналогична dragType в BaseListElementLite:
 * корневые выборки/проекции/списки → CARD_ROOT, папки и дочерние → CARD_CHILD.
 */
function dragTypeForItem(p: any): string {
  return p.isRoot && p.elType !== BaseItemTypes.FOLDER
    ? DragItemTypes.CARD_ROOT
    : DragItemTypes.CARD_CHILD;
}

/**
 * Обёртка для мульти-проверки DnD-правил: если активен режим выделения и есть выбранные элементы,
 * проверяем правило для КАЖДОГО реально выбранного элемента (AND). Дроп разрешён, только если
 * все элементы могут быть перемещены в цель. Для одиночного перетаскивания вызывается исходная функция.
 */
function multiCheckDndRule(
  singleFn: (dragParams: any, hoverParams: any, tree: BaseListItem[]) => boolean,
  dragParams: any,
  hoverParams: any,
): boolean {
  if (selectionMode.value && selectedItems.size > 0) {
    for (const sel of selectedItems.values()) {
      const dp = { type: dragTypeForItem(sel), callbackParams: sel };
      if (!singleFn(dp, hoverParams, props.inputItems)) return false;
    }
    return true;
  }
  return singleFn(dragParams, hoverParams, props.inputItems);
}

// --- DnD: обёртки над чистыми функциями из dndRules.ts ---
// При мульти-выделении проверяем правила для каждого выбранного элемента (AND).

const intoForbiddenFn = (dragParams: any, hoverParams: any) => {
  if (isFolderDropForbidden(dragParams, hoverParams, props.inputItems)) return true;
  if (selectionMode.value && selectedItems.size > 0) {
    const hoverEl = hoverParams?.callbackParams ?? hoverParams;
    for (const sel of selectedItems.values()) {
      if (isImmediateParentByIdentity(props.inputItems, sel, hoverEl)) return true;
      if (isFolderDropForbidden(
        { callbackParams: sel, type: dragTypeForItem(sel) },
        hoverParams,
        props.inputItems,
      )) return true;
    }
  }
  return false;
};

const allowChildToChildDropAction = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowChildToChildDropAction, dragParams, hoverParams);

const allowChildToRootDropAction = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowChildToRootDropAction, dragParams, hoverParams);

const allowRootToRootDropAction = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowRootToRootDropAction, dragParams, hoverParams);

const allowRootToChildDropAction = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowRootToChildDropAction, dragParams, hoverParams);

const allowHighlightOnHover = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowHighlightOnHover, dragParams, hoverParams);

// --- Flatten tree ---

interface FlatItem {
  item: BaseListItem;
  depth: number;
  isRoot: boolean;
  levelSiblings: BaseListItem[];
  /** Локальный индекс элемента среди siblings того же уровня (а не в плоском виртуальном списке). */
  siblingIndex: number;
  parentId: number | undefined;
  parentType: BaseItemTypes | undefined;
  hasChildren: boolean;
  effectiveExpanded: boolean;
  inReportContext: boolean;
  key: string;
}

interface FavoriteTreeState {
  visibleKeys: Set<string>;
  autoExpandKeys: Set<string>;
}

type ModeName = 'search' | 'favorite';

interface ModeSession {
  active: boolean;
  snapshot: Map<string, boolean> | null;
  dirty: Set<string>;
}

/**
 * Независимые сессии для search/favorite.
 * Каждый режим хранит собственный snapshot и dirty, чтобы вести себя как отдельный режим.
 */
const modeSessions = reactive<Record<ModeName, ModeSession>>({
  search: { active: false, snapshot: null, dirty: new Set<string>() },
  favorite: { active: false, snapshot: null, dirty: new Set<string>() },
});

/**
 * Текущий режим (последний включённый). Только в него пишем ручные dirty-изменения.
 */
const currentMode = ref<ModeName | null>(null);

function walkTree(items: BaseListItem[], onNode: (item: BaseListItem) => void) {
  for (const item of items) {
    onNode(item);
    for (const arrKey of CHILD_ARRAYS) {
      const arr = (item as any)[arrKey] as BaseListItem[] | undefined;
      if (!arr || arr.length === 0) continue;
      walkTree(arr, onNode);
    }
  }
}

function captureExpandSnapshot(): Map<string, boolean> {
  const snapshot = new Map<string, boolean>();
  walkTree(props.inputItems, (item) => {
    snapshot.set(makeSelectionKey(item), !!item.isExpanded);
  });
  return snapshot;
}

function startModeSession(mode: ModeName) {
  const session = modeSessions[mode];
  session.active = true;
  session.snapshot = captureExpandSnapshot();
  session.dirty = new Set();
  currentMode.value = mode;
}

function restoreModeSession(mode: ModeName) {
  const session = modeSessions[mode];
  const snapshot = session.snapshot;
  if (!snapshot) return;
  const dirty = session.dirty;

  // Merge-логика: ручные изменения пользователя (dirty) имеют приоритет над snapshot.
  walkTree(props.inputItems, (item) => {
    const key = makeSelectionKey(item);
    if (dirty.has(key)) return;
    if (!snapshot.has(key)) return;
    item.isExpanded = !!snapshot.get(key);
  });

  session.snapshot = null;
  session.dirty = new Set();
  session.active = false;
}

function applyModeAutologic(mode: ModeName) {
  // Для search отдельного "применения" не нужно: _searchAutoExpand учитывается в flattenTree.
  if (mode === 'favorite') {
    applyFavoriteAutoExpandOnce(props.inputItems, favoriteTreeState.value.autoExpandKeys);
  }
}

function otherMode(mode: ModeName): ModeName {
  return mode === 'search' ? 'favorite' : 'search';
}

function stopModeSession(mode: ModeName) {
  const session = modeSessions[mode];
  if (!session.active) return;

  // Если выключается не текущий режим, дерево не трогаем.
  // Это сохраняет ожидаемое поведение: search(on)->favorite(on)->search(off) без визуальных изменений.
  if (currentMode.value !== mode) {
    session.active = false;
    session.snapshot = null;
    session.dirty = new Set();
    return;
  }

  restoreModeSession(mode);
  const fallback = otherMode(mode);
  if (modeSessions[fallback].active) {
    currentMode.value = fallback;
    // Оставшийся активный режим применяем как "единственный".
    applyModeAutologic(fallback);
    return;
  }
  currentMode.value = null;
}

function markExpandDirtyByUser(item: BaseListItem) {
  if (!currentMode.value) return;
  const session = modeSessions[currentMode.value];
  if (!session.active) return;
  session.dirty.add(makeSelectionKey(item));
}

const isSearchModeActive = computed(() => !!props.searchString);

watch(
  () => props.searchString,
  (nextValue, prevValue) => {
    if (nextValue === prevValue) return;
    if (scrollEl.value) {
      scrollEl.value.scrollTop = 0;
    }
  },
);

watch(isSearchModeActive, (enabled, wasEnabled) => {
  if (enabled && !wasEnabled) {
    startModeSession('search');
    return;
  }
  if (!enabled && wasEnabled) {
    stopModeSession('search');
    const selected = toRaw(genstore.selectedItem as any);
    if (selected && selected.elType !== undefined) {
      // Используем уже существующую deep-link цепочку:
      // programmatic select (с раскрытием пути) + trigger scroll к selection.
      const selectedIdentity = {
        Id: selected.Id,
        FolderId: selected.FolderId,
        SrcType: selected.SrcType,
        DestType: selected.DestType,
        elType: selected.elType,
        ElementId: selected.ElementId,
      } as BaseListItem;
      genstore.setSelectedAfter(selectedIdentity, true);
      genstore.applyElementAction(selectedIdentity, 'select', true, false, 'programmatic');
    }
  }
}, { immediate: true });

function applyFavoriteAutoExpandOnce(items: BaseListItem[], autoExpandKeys: Set<string>) {
  for (const item of items) {
    const key = makeSelectionKey(item);
    if (autoExpandKeys.has(key)) {
      item.isExpanded = true;
    }
    for (const arrKey of CHILD_ARRAYS) {
      const arr = (item as any)[arrKey] as BaseListItem[] | undefined;
      if (!arr || arr.length === 0) continue;
      applyFavoriteAutoExpandOnce(arr, autoExpandKeys);
    }
  }
}

const favoriteTreeState = computed<FavoriteTreeState>(() => {
  const visibleKeys = new Set<string>();
  const autoExpandKeys = new Set<string>();

  if (!props.favoriteMode) {
    return { visibleKeys, autoExpandKeys };
  }

  const walk = (items: BaseListItem[], hasFavoriteAncestor: boolean): boolean => {
    let branchHasFavorite = false;

    for (const item of items) {
      const key = makeSelectionKey(item);
      const isFavorite = !!(item as any).IsFavorite;

      let hasFavoriteChild = false;
      for (const arrKey of CHILD_ARRAYS) {
        const arr = (item as any)[arrKey] as BaseListItem[] | undefined;
        if (!arr || arr.length === 0) continue;
        if (walk(arr, hasFavoriteAncestor || isFavorite)) {
          hasFavoriteChild = true;
        }
      }

      const subtreeHasFavorite = isFavorite || hasFavoriteChild;
      const visibleByFavorite = hasFavoriteAncestor || subtreeHasFavorite;
      if (visibleByFavorite) {
        visibleKeys.add(key);
      }

      if (hasFavoriteChild) {
        autoExpandKeys.add(key);
      }

      if (subtreeHasFavorite) {
        branchHasFavorite = true;
      }
    }

    return branchHasFavorite;
  };

  walk(props.inputItems, false);
  return { visibleKeys, autoExpandKeys };
});

// Важно: favoriteTreeState должен быть объявлен выше этого watcher с immediate,
// иначе при старте возможна TDZ-ошибка (Cannot access before initialization).
watch(() => props.favoriteMode, (enabled) => {
  // Авторазворачивание по избранному применяем только в момент включения режима.
  if (enabled) {
    if (!modeSessions.favorite.active) {
      startModeSession('favorite');
    }
    applyFavoriteAutoExpandOnce(props.inputItems, favoriteTreeState.value.autoExpandKeys);
    return;
  }
  if (modeSessions.favorite.active) {
    stopModeSession('favorite');
  }
}, { immediate: true });

function countChildren(el: any): number {
  let n = 0;
  for (const key of CHILD_ARRAYS) {
    const arr = el[key];
    if (arr) n += arr.length;
  }
  return n;
}

function getOrderedChildrenForRender(parent: any): BaseListItem[] {
  // Порядок детей берём строго из массивов данных (CHILD_ARRAYS) — они и есть источник истины.
  // Не пересортировываем по типу на рендере: серверный FolderChildren уже отсортирован, а
  // локально созданные/перемещённые элементы должны оставаться там, куда их положили мутации стора
  // (созданный — по центру вьюпорта, перемещённый — на свою отсортированную позицию).
  const result: BaseListItem[] = [];
  for (const arrKey of CHILD_ARRAYS) {
    const arr = (parent as any)[arrKey] as BaseListItem[] | undefined;
    if (!arr || arr.length === 0) continue;
    for (const child of arr) result.push(child);
  }
  return result;
}

/** isRoot здесь — только «буквально корневой» (depth === 0, без родителя). «Корневая выборка по смыслу» (в т.ч. выборка в папке) не меняется, считается в allowRootToRootDropAction. */
function flattenTree(
  items: BaseListItem[],
  depth: number,
  isRoot: boolean,
  parentId: number | undefined,
  parentType: BaseItemTypes | undefined,
  searchActive: boolean,
  favoriteActive: boolean,
  inReportContext: boolean,
): FlatItem[] {
  const result: FlatItem[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    const itemKey = makeSelectionKey(item);
    if (favoriteActive && !favoriteTreeState.value.visibleKeys.has(itemKey)) continue;
    if (searchActive && toRaw(item)._searchVisible === false) continue;

    const childCount = countChildren(item);
    const hasChildren = childCount > 0;
    const searchAutoExpand = searchActive && (item as any)._searchAutoExpand === true;
    const shouldExpand = searchActive
      ? (item.isExpanded || searchAutoExpand)
      : item.isExpanded;

    // Узел считается «в отчётном контексте», если он сам REPORT (корень)
    // или если унаследован inReportContext от родителя (любой потомок отчёта).
    const isReportRoot = item.elType === BaseItemTypes.REPORT;
    const itemInReportContext = inReportContext || isReportRoot;

    result.push({
      item,
      depth,
      isRoot,
      levelSiblings: items,
      siblingIndex: i,
      parentId,
      parentType,
      hasChildren,
      effectiveExpanded: !!shouldExpand && hasChildren,
      inReportContext: itemInReportContext,
      key: `${item.elType}_${item.Id ?? 0}_${item.FolderId ?? 0}_${item.SrcType ?? ''}_${item.DestType ?? ''}_d${depth}_i${i}`,
    });

    // В режиме поиска разворачиваем элемент, если он был раскрыт пользователем
    // ИЛИ если у него есть совпадения в поддереве (_searchAutoExpand, устанавливается в applyAnalyticsSearchFilter).
    // В режиме избранного авто-раскрытие применяется только один раз при включении режима
    // (через applyFavoriteAutoExpandOnce), далее учитывается только item.isExpanded.
    if (hasChildren && shouldExpand) {
      const orderedChildren = getOrderedChildrenForRender(item);
      const parentIdVal = item.elType === BaseItemTypes.FOLDER ? item.FolderId : item.Id;
      const sub = flattenTree(orderedChildren, depth + 1, false, parentIdVal, item.elType, searchActive, favoriteActive, itemInReportContext);
      for (let j = 0; j < sub.length; j++) result.push(sub[j]);
    }
  }
  return result;
}

// Используется в шаблоне для показа плейсхолдера «Нет совпадений» при пустом результате поиска.
// Локальная одноимённая переменная внутри flatItems оставлена как есть, чтобы не менять её область видимости.
const searchActive = computed(() => !!props.searchString);

const flatItems = computed<FlatItem[]>(() => {
  searchVersionRef.value; // reactive trigger for search flag changes
  const searchActive = !!props.searchString;
  const favoriteActive = !!props.favoriteMode;
  const result = flattenTree(props.inputItems, 0, true, undefined, undefined, searchActive, favoriteActive, false);
  return result;
});

const rowVirtualizer = useVirtualizer(computed(() => ({
  count: flatItems.value.length,
  getScrollElement: () => scrollEl.value,
  estimateSize: () => 28,
  overscan: 10,
})));

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());
const totalSize = computed(() => rowVirtualizer.value.getTotalSize());

// После резкой смены сегмента virtualizer может кратко вернуть старые индексы.
// Отсекаем их, чтобы шаблон не читал flatItems[index] как undefined.
const safeVirtualRows = computed(() => {
  return virtualRows.value
    .map((vRow: any) => ({ vRow, flat: flatItems.value[vRow.index] }))
    .filter((row: any) => !!row.flat);
});

function isSameSelectedIdentity(item: BaseListItem, selected: BaseListItem): boolean {
  if (!item || !selected) return false;
  if (selected.elType === BaseItemTypes.FOLDER) {
    return item.elType === BaseItemTypes.FOLDER &&
      (item as any).FolderId === (selected as any).FolderId;
  }
  return item.elType === selected.elType &&
    item.Id === selected.Id &&
    ((selected.SrcType != null) ? item.SrcType === selected.SrcType : !item.SrcType) &&
    item.DestType === selected.DestType &&
    ((!selected.ElementId && !item.ElementId) || item.ElementId === selected.ElementId);
}

function scrollToSelectedRow(retry: number = 0) {
  const selected = genstore.selectedItem as BaseListItem;
  if (!selected) return;
  const hasSelectableIdentity =
    (selected.elType === BaseItemTypes.FOLDER && !!selected.FolderId)
    || !!selected.Id
    || !!selected.ElementId;
  if (!hasSelectableIdentity) return;

  const index = flatItems.value.findIndex((fi) => isSameSelectedIdentity(fi.item, selected));
  if (index < 0) {
    if (retry < 8) {
      setTimeout(() => scrollToSelectedRow(retry + 1), 30);
    }
    return;
  }

  // Скроллим после раскрытия веток и пересчёта virtual rows.
  nextTick(() => {
    (rowVirtualizer.value as any).scrollToIndex(index, { align: 'center' });
  });
}

watch(
  () => genstore.triggerScrollToSelection,
  (trigger) => {
    if (!trigger) return;
    scrollToSelectedRow(0);
  },
);

let _measureQueue: HTMLElement[] = [];
let _measureRaf: number | null = null;
const MEASURE_BATCH = 8;

function drainMeasureQueue() {
  _measureRaf = null;
  const chunk = _measureQueue.splice(0, MEASURE_BATCH);
  for (const e of chunk) {
    try { rowVirtualizer.value.measureElement(e); } catch { /* swallow */ }
  }
  if (_measureQueue.length > 0) {
    _measureRaf = requestAnimationFrame(drainMeasureQueue);
  }
}

function measureElement(el: any) {
  if (!el) return;
  if (!_measureQueue.includes(el)) _measureQueue.push(el);
  if (!_measureRaf) {
    _measureRaf = requestAnimationFrame(drainMeasureQueue);
  }
}

function onToggleExpand(el: BaseListItem) {
  const anyEl: any = el;
  const autoSearch = anyEl._searchAutoExpand === true;
  markExpandDirtyByUser(el);

  // Если элемент был раскрыт только из-за автопоиска (isExpanded === false, _searchAutoExpand === true),
  // то первый клик должен просто свернуть его: отключаем авторазворачивание и НЕ включаем isExpanded.
  if (!el.isExpanded && autoSearch) {
    if (autoSearch) {
      anyEl._searchAutoExpand = false;
    }
    return;
  }

  // В остальных случаях работаем как обычный toggle.
  el.isExpanded = !el.isExpanded;
  anyEl._searchAutoExpand = false;
}

// --- Зоны «в корень»: полосы слева и справа (по 16px), подсветка пунктиром при наведении ---
interface DragItem { id: string; callbackParams: any; type: string }

function canDropToRootMulti(dragEl: any): boolean {
  if (selectionMode.value && selectedItems.size > 0) {
    for (const sel of selectedItems.values()) {
      if (!canDropToRoot(sel)) return false;
    }
    return true;
  }
  return canDropToRoot(dragEl);
}

function canDropToRootForDrag(dragEl: any): boolean {
  return canDropToRootMulti(dragEl);
}

function onRootDrop(item: DragItem, dropPosition?: { x: number; y: number }) {
  const dragEl = item.callbackParams ?? item;
  if (!canDropToRootMulti(dragEl)) return;

  onChangeParent({
    item_id: item.id,
    new_parent: null,
    new_pos: 0,
    dragParams: item.callbackParams,
    hoverParams: {},
    moveTargetKind: 'root',
    dropPosition: dropPosition ?? { ...lastClickPos.value },
  });
}

function onChangeParent(params: any) {
  emit('changeParent', params);
}
const [rootDropLeftCollected, rootDropLeftRef] = useDrop<DragItem, void, { isOver: boolean }>({
  accept: [DragItemTypes.CARD_ROOT, DragItemTypes.CARD_CHILD],
  drop(item: DragItem, monitor) {
    const dragEl = item.callbackParams ?? item;
    if (!canDropToRootMulti(dragEl)) return;
    const dropOffset = monitor.getClientOffset();
    onRootDrop(item, dropOffset ? { x: dropOffset.x, y: dropOffset.y } : undefined);
  },
  collect: (monitor) => {
    const item = monitor.getItem() as DragItem | null;
    const dragEl = item?.callbackParams ?? item;
    const allowed = !!dragEl && canDropToRootMulti(dragEl);
    return { isOver: allowed && monitor.isOver() };
  },
});
const [rootDropRightCollected, rootDropRightRef] = useDrop<DragItem, void, { isOver: boolean }>({
  accept: [DragItemTypes.CARD_ROOT, DragItemTypes.CARD_CHILD],
  drop(item: DragItem, monitor) {
    const dragEl = item.callbackParams ?? item;
    if (!canDropToRootMulti(dragEl)) return;
    const dropOffset = monitor.getClientOffset();
    onRootDrop(item, dropOffset ? { x: dropOffset.x, y: dropOffset.y } : undefined);
  },
  collect: (monitor) => {
    const item = monitor.getItem() as DragItem | null;
    const dragEl = item?.callbackParams ?? item;
    const allowed = !!dragEl && canDropToRootMulti(dragEl);
    return { isOver: allowed && monitor.isOver() };
  },
});
const rootDropLeftOver = computed(() => rootDropLeftCollected.value?.isOver ?? false);
const rootDropRightOver = computed(() => rootDropRightCollected.value?.isOver ?? false);

// --- Расчёт оптимальной позиции вставки нового элемента (по центру видимой области) ---

/**
 * Сравнение identity элемента из flatItem с переданным parentIdentity.
 * Для папок: elType === FOLDER && FolderId === FolderId
 * Для остальных: elType + Id + SrcType + DestType (строго, без упрощений)
 */
function matchIdentity(item: BaseListItem, identity: any): boolean {
  if (!item || !identity) return false;
  if (identity.elType === BaseItemTypes.FOLDER) {
    return item.elType === BaseItemTypes.FOLDER &&
      (item as any).FolderId === identity.FolderId;
  }
  return item.elType === identity.elType &&
    item.Id === identity.Id &&
    ((identity.SrcType != null) ? (item as any).SrcType === identity.SrcType : !(item as any).SrcType) &&
    (item as any).DestType === identity.DestType;
}

/**
 * Синхронно рассчитывает оптимальную позицию вставки нового элемента по центру видимой области.
 *
 * Функция СИНХРОННАЯ — вызывается ДО async API-запроса на создание, пока вьюпорт стабилен.
 * Это исключает гонку: позиция фиксируется до того, как данные начнут меняться.
 *
 * Алгоритм:
 * 1. Находит всех siblings на целевом depth (без фильтрации по типу — учитываются графики,
 *    выборки, папки и т.д. вперемешку, как они идут визуально).
 * 2. Для каждого sibling считает середину его ПОЛНОГО блока (строка + все раскрытые потомки),
 *    а не только строку-заголовок. Так раскрытый сосед с потомками учитывается целиком.
 * 3. Находит промежуток между серединами блоков, ближайший к центру вьюпорта.
 * 4. Возвращает глобальную позицию (0-based среди ВСЕХ потомков). Пересчёт в позицию
 *    внутри конкретного массива (Charts, FolderChildren и т.д.) делает insertElementLocally.
 *
 * @param parentIdentity — identity родителя (null = корень). Строго по полной identity.
 * @param newElType — тип создаваемого элемента (не используется для расчёта, но передаётся для единообразия API)
 * @returns глобальная позиция вставки
 */
function calcOptimalInsertPosition(parentIdentity: any | null, newElType: BaseItemTypes): number {
  const items = flatItems.value;
  if (items.length === 0) return 0;

  const el = scrollEl.value;
  if (!el) return 0;

  const viewportCenter = el.scrollTop + el.clientHeight / 2;

  let targetDepth: number;
  let siblingStartIdx: number;
  let siblingEndIdx: number;

  if (parentIdentity === null) {
    targetDepth = 0;
    siblingStartIdx = 0;
    siblingEndIdx = items.length;
  } else {
    let parentFlatIdx = -1;
    for (let i = 0; i < items.length; i++) {
      if (matchIdentity(items[i].item, parentIdentity)) {
        parentFlatIdx = i;
        break;
      }
    }
    if (parentFlatIdx < 0) return 0;

    targetDepth = items[parentFlatIdx].depth + 1;
    siblingStartIdx = parentFlatIdx + 1;
    siblingEndIdx = items.length;
    for (let i = siblingStartIdx; i < items.length; i++) {
      if (items[i].depth <= items[parentFlatIdx].depth) {
        siblingEndIdx = i;
        break;
      }
    }
  }

  // Собрать ВСЕ siblings на targetDepth (без фильтрации по типу)
  interface SiblingInfo { flatIdx: number; globalOrder: number; }
  const siblings: SiblingInfo[] = [];
  let globalOrder = 0;
  for (let i = siblingStartIdx; i < siblingEndIdx; i++) {
    if (items[i].depth !== targetDepth) continue;
    siblings.push({ flatIdx: i, globalOrder });
    globalOrder++;
  }

  if (siblings.length === 0) return 0;

  // Y-позиции из виртуализатора: getVirtualItems() возвращает отрендеренные строки (видимые + overscan).
  // Берём ВЕРХ строки (start). Для строк вне виртуального окна — грубая оценка flatIdx * 28 (estimateSize).
  const vRows = rowVirtualizer.value.getVirtualItems();
  const topMap = new Map<number, number>();
  for (const vr of vRows) {
    topMap.set(vr.index, vr.start);
  }
  const topY = (idx: number): number => {
    const t = topMap.get(idx);
    return t !== undefined ? t : idx * 28;
  };

  // Для каждого соседа берём середину его ПОЛНОГО блока (строка соседа + все его раскрытые потомки),
  // а не только строку-заголовок. В плоском списке потомки соседа s_i лежат строками между s_i и
  // следующим соседом того же уровня, поэтому верх следующего соседа = низ блока s_i (для последнего —
  // граница поддерева siblingEndIdx). Свёрнут → потомков нет в flatItems → блок = одна строка;
  // раскрыт → блок включает потомков, и его середина смещается вниз.
  const siblingYs: number[] = siblings.map((s, k) => {
    const blockTop = topY(s.flatIdx);
    const blockBottomIdx = (k + 1 < siblings.length) ? siblings[k + 1].flatIdx : siblingEndIdx;
    const blockBottom = topY(blockBottomIdx);
    return (blockTop + blockBottom) / 2;
  });

  // Ищем промежуток, ближайший к центру вьюпорта.
  // Для N siblings есть N+1 позиций вставки: 0=перед первым, i=между [i-1] и [i], N=после последнего.
  // Крайние случаи: если центр выше всех siblings → 0 (в начало), ниже всех → N (в конец).
  if (viewportCenter <= siblingYs[0]) return 0;
  if (viewportCenter >= siblingYs[siblingYs.length - 1]) return siblings.length;

  for (let i = 0; i < siblingYs.length - 1; i++) {
    if (viewportCenter >= siblingYs[i] && viewportCenter <= siblingYs[i + 1]) {
      return i + 1;
    }
  }

  return Math.floor(siblings.length / 2);
}

defineExpose({
  calcOptimalInsertPosition,
  selectionMode,
  enterSelectionMode,
  exitSelectionMode,
  getSelectedIdentities,
  getEffectiveSelectedIdentities,
  selectedItems,
  flatItems,
  effectiveSelectedCount,
});
</script>

<style lang="scss" scoped>
.virtual-tree-root {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.selection-panel {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  height: 30px;
  flex-shrink: 0;
  padding: 8px 16px 0 30px;
  background: #fff;

  &__left {
    display: flex;
    gap: 16px;
  }

  &__btn {
    color: var(--Text-color-text-secondary, #979797);
    font-family: Roboto, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    cursor: pointer;
    user-select: none;

    &:hover {
      color: var(--Text-color-text-secondary, #979797);
      text-decoration-line: underline;
    }

    &:active {
      color: var(--Text-color-text-secondary, #979797);
    }
  }
}

.virtual-tree-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

// Плейсхолдер «Нет совпадений» при пустом результате поиска. 12px — от верхней
// границы компонента (под внешней шапкой .list-toolbar в Analytics.vue).
.virtual-tree__no-results {
  color: #4D4D4D;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  text-align: center;
  padding-top: 12px;
}

.root-drop-strip {
  width: 16px;
  flex-shrink: 0;
  /* Зона дропа «в корень»; паддинг 16px сохраняется за счёт ширины полос. */
  border: 1px dashed transparent;
  border-radius: 4px;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &--over {
    border-color: #90a4ae;
    background-color: rgba(144, 164, 174, 0.08);
  }
}

/* Right strip is inside scroll area; compensate vertical scroll paddings. */
.root-drop-strip--right {
  margin-top: calc(-1 * var(--vts-pad-top, 12px));
  margin-bottom: calc(-1 * var(--vts-pad-bottom, 12px));
}

.virtual-tree-scroll {
  --vts-pad-top: 12px;
  --vts-pad-bottom: 12px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--vts-pad-top) 4px var(--vts-pad-bottom);
  min-width: 0;
  /* Тонкий скролл как в lite-таблице. Firefox. */
  scrollbar-width: thin;
  scrollbar-color: #d0d0d0 transparent;

  &--compact {
    --vts-pad-top: 0px;
  }
}

/* WebKit/Blink: 6px, серый бегунок, прозрачная дорожка — как у таблицы. */
.virtual-tree-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.virtual-tree-scroll::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 6px;
}

.virtual-tree-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.virtual-tree-scroll-inner {
  display: flex;
  min-width: min-content;
}

.virtual-tree-scroll-content {
  flex: 1;
  min-width: 0;
  position: relative;
}

// Плавный сдвиг строк при вставке/удалении элемента. Класс добавляется только на время анимации
// (через insertAnimActive), чтобы при обычном скролле transition не вызывал визуальных задержек.
.vrow-animate {
  transition: transform 0.3s ease;
}
</style>
